import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import {
  FileText,
  TrendingUp,
  DollarSign,
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  ArrowRight,
  Eye
} from 'lucide-react';
import BlurredValue from '../../../components/common/BlurredValue';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const WholesaleReportsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardSummary();
  }, []);

  const fetchDashboardSummary = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('accessToken');

      const response = await axios.get(`${API_URL}/reports/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { module: 'wholesale' } // Filter by wholesale module
      });

      setDashboardData(response.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch wholesale report data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  const financialReports = [
    {
      id: 'income-statement',
      title: 'Wholesale P&L',
      subtitle: 'Income Statement',
      description: 'Revenue, expenses, and profitability for wholesale operations',
      icon: TrendingUp,
      color: 'bg-emerald-500',
      hoverColor: 'hover:bg-emerald-600',
      path: '/wholesale/reports/income-statement',
      metrics: dashboardData
        ? [
            {
              label: 'Total Revenue',
              value: formatCurrency(dashboardData.income_statement.current.revenue.total)
            },
            {
              label: 'Net Profit',
              value: formatCurrency(dashboardData.income_statement.current.net_profit_after_tax)
            },
            {
              label: 'Profit Margin',
              value: formatPercentage(dashboardData.income_statement.current.net_profit_margin)
            }
          ]
        : []
    },
    {
      id: 'balance-sheet',
      title: 'Wholesale Balance Sheet',
      subtitle: 'Financial Position',
      description: 'Assets, liabilities, and equity specifically for the wholesale module',
      icon: BarChart3,
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      path: '/wholesale/reports/balance-sheet',
      metrics: dashboardData
        ? [
            {
              label: 'Total Assets',
              value: formatCurrency(dashboardData.balance_sheet.current.assets.total)
            },
            {
              label: 'Total Liabilities',
              value: formatCurrency(dashboardData.balance_sheet.current.liabilities.total)
            },
            {
              label: 'Total Equity',
              value: formatCurrency(dashboardData.balance_sheet.current.equity.total)
            }
          ]
        : []
    },
    {
      id: 'cash-flow',
      title: 'Wholesale Cash Flow',
      subtitle: 'Statement of Cash Flows',
      description: 'Cash inflows and outflows from wholesale activities',
      icon: Activity,
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      path: '/wholesale/reports/cash-flow',
      metrics: dashboardData
        ? [
            {
              label: 'Beginning Cash',
              value: formatCurrency(dashboardData.cash_flow.beginning_cash)
            },
            {
              label: 'Operating Cash Flow',
              value: formatCurrency(dashboardData.cash_flow.operating_activities.net_cash)
            },
            { label: 'Ending Cash', value: formatCurrency(dashboardData.cash_flow.ending_cash) }
          ]
        : []
    }
  ];

  const otherReports = [
    {
      id: 'sales-analysis',
      title: 'Sales Analysis',
      subtitle: 'Wholesale Trends',
      description: 'Analyze wholesale orders by company, product, and distributor',
      icon: DollarSign,
      color: 'bg-amber-500',
      hoverColor: 'hover:bg-amber-600',
      path: '/wholesale/reports/sales'
    },
    {
      id: 'inventory-report',
      title: 'Inventory Value',
      subtitle: 'Stock Valuation',
      description: 'Track wholesale stock levels and valuation across manufacturing companies',
      icon: PieChart,
      color: 'bg-indigo-500',
      hoverColor: 'hover:bg-indigo-600',
      path: '/wholesale/reports/inventory-valuation'
    },
    {
      id: 'ar-aging',
      title: 'Distributor Aging',
      subtitle: 'Accounts Receivable',
      description: 'Track outstanding balances from wholesale distributors',
      icon: Calendar,
      color: 'bg-pink-500',
      hoverColor: 'hover:bg-pink-600',
      path: '/wholesale/reports/ar-aging',
      comingSoon: true
    },
    {
      id: 'ap-aging',
      title: 'Wholesale AP Aging',
      subtitle: 'Accounts Payable',
      description: 'Track outstanding bills to wholesale suppliers/vendors',
      icon: Calendar,
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      path: '/wholesale/reports/ap-aging',
      comingSoon: true
    }
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Wholesale Financial Reports
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Comprehensive financial insights for wholesale module activities
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Quick Stats */}
        {dashboardData && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm opacity-90">Wholesale Revenue</div>
                <TrendingUp className="h-5 w-5 opacity-90" />
              </div>
              <div className="text-xl md:text-2xl lg:text-3xl font-bold">
                <BlurredValue
                  value={formatCurrency(dashboardData.income_statement.current.revenue.total)}
                  className="text-white"
                />
              </div>
              <div className="text-xs opacity-75 mt-1">This month</div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm opacity-90">Wholesale Assets</div>
                <BarChart3 className="h-5 w-5 opacity-90" />
              </div>
              <div className="text-xl md:text-2xl lg:text-3xl font-bold">
                <BlurredValue
                  value={formatCurrency(dashboardData.balance_sheet.current.assets.total)}
                  className="text-white"
                />
              </div>
              <div className="text-xs opacity-75 mt-1">As of today</div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm opacity-90">Module Cash</div>
                <Activity className="h-5 w-5 opacity-90" />
              </div>
              <div className="text-xl md:text-2xl lg:text-3xl font-bold">
                <BlurredValue
                  value={formatCurrency(dashboardData.cash_flow.ending_cash)}
                  className="text-white"
                />
              </div>
              <div className="text-xs opacity-75 mt-1">Current balance</div>
            </div>

            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm opacity-90">Net Profit</div>
                <DollarSign className="h-5 w-5 opacity-90" />
              </div>
              <div className="text-xl md:text-2xl lg:text-3xl font-bold">
                <BlurredValue
                  value={formatCurrency(
                    dashboardData.income_statement.current.net_profit_after_tax
                  )}
                  className="text-white"
                />
              </div>
              <div className="text-xs opacity-75 mt-1">
                <BlurredValue
                  value={formatPercentage(dashboardData.income_statement.current.net_profit_margin)}
                  className="text-white"
                />{' '}
                margin
              </div>
            </div>
          </div>
        )}

        {/* Core Financial Reports */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Wholesale Statements</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {financialReports.map((report) => {
              const Icon = report.icon;
              return (
                <div
                  key={report.id}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`${report.color} p-3 rounded-lg text-white`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-1">{report.title}</h3>
                    <p className="text-sm text-gray-500 mb-3">{report.subtitle}</p>
                    <p className="text-sm text-gray-600 mb-4">{report.description}</p>

                    {/* Metrics */}
                    {report.metrics && report.metrics.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {report.metrics.map((metric, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center text-xs md:text-sm"
                          >
                            <span className="text-gray-600">{metric.label}:</span>
                            <span className="font-semibold text-gray-900">
                              <BlurredValue value={metric.value} />
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={() => navigate(report.path)}
                      className={`w-full ${report.color} ${report.hoverColor} text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors`}
                    >
                      <Eye className="h-4 w-4" />
                      View Report
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Other Reports */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Module Specific Reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {otherReports.map((report) => {
              const Icon = report.icon;
              return (
                <div
                  key={report.id}
                  className={`bg-white rounded-lg shadow border border-gray-200 ${
                    report.comingSoon
                      ? 'opacity-60'
                      : 'hover:shadow-lg transition-shadow cursor-pointer'
                  }`}
                  onClick={() => !report.comingSoon && navigate(report.path)}
                >
                  <div className="p-5">
                    <div className={`${report.color} p-3 rounded-lg text-white w-fit mb-3`}>
                      <Icon className="h-5 w-5" />
                    </div>

                    <h3 className="text-base font-bold text-gray-900 mb-1">{report.title}</h3>
                    <p className="text-xs text-gray-500 mb-2">{report.subtitle}</p>
                    <p className="text-xs text-gray-600">{report.description}</p>

                    {report.comingSoon && (
                      <div className="mt-3">
                        <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                          Coming Soon
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Report Info Section */}
        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-emerald-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-emerald-900 mb-2">Wholesale Financial Reporting</h3>
              <div className="text-sm text-emerald-800 space-y-2">
                <p>
                  Financial reports in the Wholesale module are isolated to transactions marked as
                  'Wholesale'. This includes sales from manufacturing companies, wholesale expenses,
                  and related journal entries.
                </p>
                <p>
                  <strong>Revenue Isolation:</strong> Only wholesale orders and invoices are
                  included in revenue metrics.
                </p>
                <p>
                  <strong>Expense Isolation:</strong> Only expenses submitted through the wholesale
                  module are included in profit calculations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesaleReportsPage;
